/**
 * Analytics identity predicate (platform-agnostic).
 *
 * Rule: an analytics client is reset only when leaving an identified state.
 * A persisted anonymous id must survive signed-out cold launches, otherwise
 * every launch of a signed-out install becomes a "new" person. (Measured
 * impact and the PostHog evidence: PR #94 / commit 59807a3.)
 *
 * Whether the client is identified is decided the way the SDK decides it:
 * after `identify()`, PostHog stores the previous anonymous id as
 * `AnonymousId` and the user id as `DistinctId`, so the two differ; on a fresh
 * store, and after `reset()`, `getDistinctId()` falls back to the anonymous id
 * and they are equal. No id-shape assumptions (UUID version, uid length), so an
 * SDK bump that changes how anonymous ids are minted cannot break this.
 *
 * Callers pass the SDK's current `getDistinctId()` / `getAnonymousId()`; the
 * SDK returns `''` for both until its async storage preload has finished, so
 * callers must `await posthog.ready()` first. Blank or non-string ids are
 * reported as NOT identified: there is nothing to detach, and resetting on an
 * unknown state would be the original bug.
 */

/**
 * @param {unknown} distinctId  - `posthog.getDistinctId()`
 * @param {unknown} anonymousId - `posthog.getAnonymousId()`
 * @returns {boolean} true when the client currently carries an identified user id
 */
export function isIdentifiedDistinctId(distinctId, anonymousId) {
  if (typeof distinctId !== 'string' || typeof anonymousId !== 'string') return false;
  if (distinctId.trim() === '' || anonymousId.trim() === '') return false;
  return distinctId !== anonymousId;
}

/**
 * Web counterpart for posthog-js, which tracks identity as a persisted
 * `$user_state`: `'identified'` after `identify()`, `'anonymous'` after
 * `reset()`, and unset for a first-time visitor. Its own `_isIdentified()` is
 * exactly this comparison. Anything other than the literal `'identified'`
 * (unset, `'anonymous'`, non-string) reports NOT identified, so a fresh or
 * signed-out visitor is never reset on page load.
 *
 * @param {unknown} userState - `posthog.get_property('$user_state')`
 * @returns {boolean}
 */
export function isIdentifiedUserState(userState) {
  return userState === 'identified';
}
